import { Injectable, computed, signal } from '@angular/core';
import LogRocket from 'logrocket';
import { ParticipantService } from '../participant/participant.service';

/**
 * Owns the Daily pre-join connection ("tech check") test so it can be started
 * early on the welcome page and its result read later on the setup page.
 * Previously this lived inline in SetupComponent; it was extracted to a root
 * singleton so both routed components share one run and one result.
 *
 * State is exposed as signals — components read them directly, so OnPush views
 * re-render automatically when the test progresses (no manual change detection).
 *
 * The test requires a live local video track (startCamera) and must run before
 * join — the same preconditions Daily's testCallQuality() documents.
 */
@Injectable({
    providedIn: 'root',
})
export class ConnectionTestService {
    /** Result of Daily testCallQuality(): 'good' | 'warning' | 'bad' | 'failed' | 'aborted' | 'unknown' */
    readonly peerSocketNetworkQuality = signal<any>(undefined);
    /** Result of Daily testNetworkConnectivity() (TURN reachability) */
    readonly networkConnectivityResult = signal<any>(undefined);
    /** True once a test run has completed (success or failure) */
    readonly hasTestConnectionRun = signal(false);
    /** True while a test run is in flight */
    readonly checkNetworkQualityLoading = signal(false);
    /** Latches true once a warning/bad result is seen, for persistent warnings */
    readonly hasPoorConnectionBeenDetected = signal(false);
    /** Drives the legacy setup UI's "show quality" toggle */
    readonly isNetworkQualityVisible = signal(false);

    // Progress UI state, surfaced on the setup card while the test runs.
    readonly timer = signal(0);
    readonly progressText = signal('');
    readonly currentTestStage = signal(0);

    /** True when the connection is too poor to conduct an AI-moderated interview. */
    readonly hasPoorNetworkQuality = computed(
        () =>
            this.peerSocketNetworkQuality() === 'bad' ||
            this.peerSocketNetworkQuality() === 'aborted',
    );

    /** Guards against overlapping runs while one is already in flight */
    private running = false;

    /** Hard cap on Daily's testCallQuality() (docs allow up to 30s) */
    readonly TEST_TIMEOUT_MS = 15000;

    constructor(private participantService: ParticipantService) {}

    private get callObject(): any {
        return this.participantService.callObject;
    }

    private get localParticipant(): any {
        return this.participantService.localParticipant;
    }

    /**
     * Runs Daily's pre-join connection tests (testCallQuality +
     * testWebsocketConnectivity + testNetworkConnectivity) and stores the
     * results in signals. Idempotent: a no-op while a run is in flight or after
     * one has completed, unless `force` is passed (the "Re-check connection"
     * button).
     */
    async run(force = false) {
        if (force) {
            this.hasTestConnectionRun.set(false);
        }
        if (this.running || this.hasTestConnectionRun()) {
            return;
        }

        // Requires a live local video track — if the camera preview hasn't
        // started yet, surface the quality panel and bail so a later trigger
        // (e.g. the setup page) can run it once the track is ready.
        if (!this.localParticipant?.videoReady) {
            this.isNetworkQualityVisible.set(true);
            return;
        }

        this.running = true;
        // Reset result state at the start of every run so a fresh run — in
        // particular a forced re-check after the connection has recovered —
        // re-evaluates from scratch. These signals live on a root singleton, so
        // without this they would keep a previous run's latched values (e.g. a
        // stale poor-connection warning) for the whole app session.
        this.peerSocketNetworkQuality.set(undefined);
        this.networkConnectivityResult.set(undefined);
        this.hasPoorConnectionBeenDetected.set(false);
        this.timer.set(0);
        this.progressText.set('Starting network test...');
        this.checkNetworkQualityLoading.set(true);

        await new Promise((resolve) => setTimeout(resolve, 300));

        const stages = [
            { text: $localize`Initializing network test...`, progress: 5 },
            { text: $localize`Establishing peer connections...`, progress: 15 },
            { text: $localize`Testing call quality...`, progress: 25 },
            { text: $localize`Analyzing network data...`, progress: 60 },
            {
                text: $localize`Testing websocket connectivity...`,
                progress: 80,
            },
            { text: $localize`Finalizing results...`, progress: 90 },
            { text: $localize`Complete`, progress: 100 },
        ];

        this.currentTestStage.set(0);
        this.progressText.set(stages[0].text);
        this.timer.set(stages[0].progress);

        const advanceStage = () => {
            const next = Math.min(
                this.currentTestStage() + 1,
                stages.length - 1,
            );
            this.currentTestStage.set(next);
            this.progressText.set(stages[next].text);
            this.timer.set(stages[next].progress);
        };

        try {
            advanceStage();

            const qualityPromise = this.callObject?.testCallQuality();
            advanceStage();

            const timeoutId = setTimeout(() => {
                this.callObject?.stopTestCallQuality();
            }, this.TEST_TIMEOUT_MS);

            const startProgress = this.timer();
            const endProgress = stages[3].progress;
            let progressUpdateCount = 0;

            const progressUpdateInterval = setInterval(() => {
                progressUpdateCount++;

                if (progressUpdateCount % 3 === 1) {
                    this.progressText.set($localize`Testing audio connection...`);
                } else if (progressUpdateCount % 3 === 2) {
                    this.progressText.set($localize`Testing video quality...`);
                } else {
                    this.progressText.set(
                        $localize`Measuring network stability...`,
                    );
                }

                const progressStep = (endProgress - startProgress) * 0.9;
                const incrementalProgress = Math.min(
                    startProgress + progressStep * (progressUpdateCount / 12),
                    endProgress - 1,
                );
                this.timer.set(incrementalProgress);
            }, 1000);

            // Clear the progress timers in a finally so they are torn down
            // whether testCallQuality resolves OR rejects. Previously they were
            // cleared only on the success path below, so a rejection left the
            // 1s interval running forever on this root singleton.
            let quality;
            try {
                quality = await qualityPromise;
            } finally {
                clearTimeout(timeoutId);
                clearInterval(progressUpdateInterval);
            }

            advanceStage();

            const websocketResult =
                await this.callObject?.testWebsocketConnectivity();
            advanceStage();

            // The connection-quality result comes entirely from testCallQuality
            // (above) and drives both the UI and the AI join gate — so reveal it
            // now. It must NOT wait on the TURN-server reachability probe below,
            // which pings ~10 regions sequentially and only feeds diagnostics.
            this.peerSocketNetworkQuality.set(quality?.result || 'unknown');
            this.isNetworkQualityVisible.set(true);
            this.progressText.set(
                'Connection Quality: ' + this.peerSocketNetworkQuality(),
            );
            if (
                this.peerSocketNetworkQuality() === 'warning' ||
                this.peerSocketNetworkQuality() === 'bad'
            ) {
                this.hasPoorConnectionBeenDetected.set(true);
            }
            advanceStage();

            // quality / websocketResult are result objects — LogRocket.track
            // only accepts primitives and warns on every object property, so
            // send the primitive `.result` summaries instead of the raw objects.
            const logNetworkQuality = (networkConnectivity: string) => {
                console.log('Network quality:', [
                    quality,
                    websocketResult,
                    networkConnectivity,
                ]);
                LogRocket.track('Network quality:', {
                    quality: quality?.result ?? 'unknown',
                    websocketResult: websocketResult?.result ?? 'unknown',
                    networkConnectivity,
                });
            };

            // Verify reachability of Daily's TURN servers (the relay/TLS
            // fallback path) in the background — diagnostics only, so it does
            // not gate the result above. Guarded so it degrades gracefully if
            // the track or method is unavailable.
            const videoTrack =
                this.callObject?.participants?.()?.local?.tracks?.video
                    ?.persistentTrack;
            if (videoTrack) {
                Promise.resolve(
                    this.callObject?.testNetworkConnectivity?.(videoTrack),
                )
                    .then((connectivityResult: any) => {
                        this.networkConnectivityResult.set(
                            connectivityResult?.result || 'unknown',
                        );
                        logNetworkQuality(this.networkConnectivityResult());
                    })
                    .catch((error: any) => {
                        console.error(error);
                        logNetworkQuality('unknown');
                    });
            } else {
                logNetworkQuality('unknown');
            }

            // Mark the run complete only on success. A thrown run (e.g. a
            // transient pre-join error while the test was primed early on the
            // welcome page) leaves this false so the setup page's auto-trigger
            // can retry, rather than latching a permanent 'problem' state.
            this.hasTestConnectionRun.set(true);
        } catch (error) {
            console.error(error);
            this.progressText.set('Connection test failed');
        } finally {
            this.checkNetworkQualityLoading.set(false);
            this.timer.set(100);
            this.running = false;
        }
    }
}
